'use client';

import { useMemo, useState } from 'react';
import { FileText, Loader2, Search } from 'lucide-react';

import { Banner } from '@/components/ui/banner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LabelledToggleRow } from '@/components/ui/labelled-toggle-row';
import { ListItemSkeleton } from '@/components/ui/skeleton';
import { exhibitLetter } from '@/lib/services/precedent-documents/exhibit-letter';
import { usePrecedentDocumentOptions } from '@/lib/services/precedent-documents';
import type {
  PrecedentDocumentOptions,
  PrecedentExhibitOption,
  PrecedentOption
} from '@/lib/services/precedent-documents';

/**
 * Attach one of the firm's own precedents to the email being written
 * (VALE-1138).
 *
 * ## Why this exists next to "Choose existing document"
 *
 * A precedent could only reach an email by being bound to an email template in
 * advance, one precedent per template — so a firm holding two variants of the
 * same letter could not express the per-matter choice between them, and a firm
 * that never found the binding screen could not reach its own precedents from
 * the composer at all. This is the per-email pick. The binding stays as the
 * right answer for a template that always carries the same document.
 *
 * ## The precedent declares what it is asked
 *
 * Same principle as the Documents tab's generate drawer, and the reason this is
 * two steps rather than a form: the backend reads each precedent's `.docx` and
 * reports the optional clauses and the exhibit block it actually carries, so a
 * plain letter is picked and attached in one click while an affidavit asks for
 * both. A fixed form would put a clause toggle on a precedent with no clauses.
 *
 * **The addressee is not among the questions.** The email already has a
 * recipient — the person whose thread this is — and asking again would let the
 * letter be addressed to somebody other than the person receiving it.
 */
export function AttachPrecedentDialog({
  matterId,
  open,
  onOpenChange,
  recipientName,
  onAttach,
  isAttaching
}: {
  matterId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Who the email is addressed to — the addressee the precedent merges against. */
  recipientName: string;
  onAttach: (input: {
    documentTemplateId: number;
    flags?: Record<string, boolean>;
    exhibits?: { fileId: number; description: string }[];
  }) => Promise<void>;
  isAttaching: boolean;
}) {
  // Fetched only while open: this read opens every one of the firm's precedents
  // out of S3 to discover the clauses it carries, so it is markedly more
  // expensive than a list and carries its own query key for that reason.
  const {
    data: options,
    isLoading,
    isError
  } = usePrecedentDocumentOptions(matterId, open);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/*
        32rem is the design cap; the `min()` is what keeps it under a short
        viewport, since the panel is `overflow-hidden` and its own `max-h`
        override replaces the primitive's viewport clamp (VALE-1438).
      */}
      <DialogContent className="flex max-h-[min(32rem,calc(100dvh-2rem))] flex-col gap-0 overflow-hidden p-0 sm:max-w-lg">
        <DialogHeader className="border-border-subtle space-y-1 border-b px-5 py-4">
          <DialogTitle className="text-sm font-semibold">
            Attach a precedent
          </DialogTitle>
          <DialogDescription className="text-12px">
            Merged against this matter and addressed to {recipientName}. It
            attaches as an editable Word document and is sent as a PDF.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {isLoading ? (
            <div className="space-y-3">
              <ListItemSkeleton />
              <ListItemSkeleton />
            </div>
          ) : isError || !options ? (
            <Banner
              tone="critical"
              title="Your precedents could not be loaded"
              line="Close this and try again in a moment. If it keeps happening, check the precedents still open in Templates → Documents."
            />
          ) : options.precedents.length === 0 ? (
            <Banner
              tone="neutral"
              title="Your firm has no precedents yet"
              line="Upload a Word document under Templates → Documents, then come back here to attach it to an email."
            />
          ) : (
            <AttachPrecedentBody
              options={options}
              onAttach={onAttach}
              isAttaching={isAttaching}
              onDone={() => onOpenChange(false)}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AttachPrecedentBody({
  options,
  onAttach,
  isAttaching,
  onDone
}: {
  options: PrecedentDocumentOptions;
  onAttach: (input: {
    documentTemplateId: number;
    flags?: Record<string, boolean>;
    exhibits?: { fileId: number; description: string }[];
  }) => Promise<void>;
  isAttaching: boolean;
  onDone: () => void;
}) {
  const [search, setSearch] = useState('');
  // Set only for a precedent that has something to ask. A precedent declaring
  // neither clauses nor exhibits never reaches this step at all — it attaches on
  // the pick, which is the case both of Coulter's post-grant letters are in.
  const [answering, setAnswering] = useState<PrecedentOption | null>(null);
  const [flags, setFlags] = useState<Record<string, boolean>>({});
  const [exhibits, setExhibits] = useState<
    { fileId: number; description: string }[]
  >([]);

  const matches = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return options.precedents;
    return options.precedents.filter((precedent) =>
      precedent.name.toLowerCase().includes(query)
    );
  }, [options.precedents, search]);

  const attach = async (
    precedent: PrecedentOption,
    answers: {
      flags?: Record<string, boolean>;
      exhibits?: { fileId: number; description: string }[];
    } = {}
  ) => {
    await onAttach({ documentTemplateId: precedent.templateId, ...answers });
    onDone();
  };

  const pick = async (precedent: PrecedentOption) => {
    if (precedent.flags.length > 0 || precedent.usesExhibits) {
      setFlags({});
      setExhibits([]);
      setAnswering(precedent);
      return;
    }
    await attach(precedent);
  };

  if (answering) {
    const toggleExhibit = (candidate: PrecedentExhibitOption) =>
      setExhibits((current) =>
        current.some((exhibit) => exhibit.fileId === candidate.fileId)
          ? current.filter((exhibit) => exhibit.fileId !== candidate.fileId)
          : // Appended, not inserted: exhibits are lettered by position, so the
            // order the lawyer ticks them in *is* the order A, B, C.
            [...current, { fileId: candidate.fileId, description: '' }]
      );

    const undescribed = exhibits.filter(
      (exhibit) => exhibit.description.trim().length === 0
    );

    return (
      <div className="space-y-5">
        <div className="text-foreground flex items-center gap-2 text-sm font-medium">
          <FileText
            className="text-foreground-subtle h-3.5 w-3.5 shrink-0"
            aria-hidden="true"
          />
          <span className="min-w-0 truncate">{answering.name}</span>
        </div>

        {answering.flags.length > 0 && (
          <div className="space-y-3">
            <div>
              <Label>Optional clauses</Label>
              <p className="text-muted-foreground text-12px">
                Sections your precedent switches in or out. Anything left off is
                simply not included.
              </p>
            </div>
            {answering.flags.map((flag) => (
              <LabelledToggleRow
                key={flag.name}
                id={`attach-flag-${flag.name}`}
                label={flag.label}
                description={flag.description}
                checked={flags[flag.name] === true}
                onCheckedChange={(checked) =>
                  setFlags((current) => ({ ...current, [flag.name]: checked }))
                }
              />
            ))}
          </div>
        )}

        {answering.usesExhibits && (
          <div className="space-y-3">
            <div>
              <Label>Exhibits</Label>
              <p className="text-muted-foreground text-12px">
                Documents on this matter to exhibit. They are lettered A, B, C
                in the order you tick them, and each needs the description your
                certificate should identify it by.
              </p>
            </div>
            {options.exhibitCandidates.length === 0 ? (
              <p className="text-muted-foreground text-12px">
                This matter has no documents to exhibit yet.
              </p>
            ) : (
              <div className="space-y-2">
                {options.exhibitCandidates.map((candidate) => {
                  const index = exhibits.findIndex(
                    (exhibit) => exhibit.fileId === candidate.fileId
                  );
                  const selected = index >= 0;
                  return (
                    <div key={candidate.fileId} className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id={`attach-exhibit-${candidate.fileId}`}
                          checked={selected}
                          onCheckedChange={() => toggleExhibit(candidate)}
                        />
                        <Label
                          htmlFor={`attach-exhibit-${candidate.fileId}`}
                          className="text-12px font-normal"
                        >
                          {selected && (
                            <span className="mr-2 font-semibold">
                              {exhibitLetter(index)}
                            </span>
                          )}
                          {candidate.name}
                        </Label>
                      </div>
                      {/* "e.g."-prefixed deliberately: the bare phrase reads as
                          a filled value, which makes the warning beside it look
                          like a bug and the disabled button inexplicable. */}
                      {selected && (
                        <Input
                          aria-label={`Description for exhibit ${exhibitLetter(index)}`}
                          placeholder="e.g. the death certificate of the deceased"
                          value={exhibits[index].description}
                          onChange={(event) =>
                            setExhibits((current) =>
                              current.map((exhibit) =>
                                exhibit.fileId === candidate.fileId
                                  ? {
                                      ...exhibit,
                                      description: event.target.value
                                    }
                                  : exhibit
                              )
                            )
                          }
                          className="ml-6 h-8 w-[calc(100%-1.5rem)]"
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {undescribed.length > 0 && (
              <Banner
                tone="caution"
                title="Every exhibit needs a description"
                line="The certificate identifies the exhibit by what you write here, not by its filename."
              />
            )}
          </div>
        )}

        <div className="flex items-center justify-end gap-2">
          <Button
            size="xs"
            density="compact"
            type="button"
            variant="outline"
            onClick={() => setAnswering(null)}
            disabled={isAttaching}
          >
            Back
          </Button>
          <Button
            size="xs"
            density="compact"
            gap="standard"
            type="button"
            disabled={undescribed.length > 0 || isAttaching}
            onClick={() =>
              attach(answering, {
                ...(Object.keys(flags).length > 0 ? { flags } : {}),
                ...(exhibits.length > 0
                  ? {
                      exhibits: exhibits.map((exhibit) => ({
                        fileId: exhibit.fileId,
                        description: exhibit.description.trim()
                      }))
                    }
                  : {})
              })
            }
          >
            {isAttaching && (
              <Loader2
                className="h-3.5 w-3.5 animate-spin"
                aria-hidden="true"
              />
            )}
            Attach
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search
          className="text-foreground-subtle absolute top-1/2 left-2 h-3.5 w-3.5 -translate-y-1/2"
          aria-hidden="true"
        />
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Find a precedent…"
          aria-label="Find a precedent"
          className="h-8 px-2 py-0 pl-7 text-xs"
        />
      </div>

      {matches.length === 0 ? (
        <p className="text-muted-foreground py-6 text-center text-xs">
          No precedents match &ldquo;{search}&rdquo;.
        </p>
      ) : (
        <div className="flex flex-col">
          {matches.map((precedent) => (
            <button
              key={precedent.templateId}
              type="button"
              disabled={isAttaching}
              onClick={() => void pick(precedent)}
              className="hover:bg-surface-subtle flex w-full items-center gap-2 rounded px-2 py-2 text-left text-xs disabled:cursor-not-allowed disabled:opacity-50"
            >
              <FileText
                className="text-foreground-subtle h-3.5 w-3.5 shrink-0"
                aria-hidden="true"
              />
              <span className="text-foreground min-w-0 flex-1 truncate font-medium">
                {precedent.name}
              </span>
              {(precedent.flags.length > 0 || precedent.usesExhibits) && (
                <span className="text-muted-foreground text-12px shrink-0">
                  Needs details
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
